import { getPackageProviderIconUrl } from 'components/shared/ppoints/PackageProviderIcon';
import { SettingUrls } from 'constants/SettingsUrls';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import PackageProviderService from 'services/settings/ppoints/PackageProviderService';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { UsageProgressProviderRow } from './components/UsageProgressProviderRow';
import { useFirstSelectedProvider } from './hooks/useFirstSelectedProvider';
import { UsageProgressAction, UsageProgressCardProps } from './models/usage-progress-card-props';

const ActionButton: React.FC<{ action: UsageProgressAction }> = ({ action: { label, ...buttonProps } }) => (
    <s-button {...buttonProps}>{label}</s-button>
);

export const UsageProgressCard: React.FC<UsageProgressCardProps> = ({
    title,
    statusBadge,
    usageText,
    planName,
    progress,
    description,
    carrierLimit,
    actions,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { currentSubscription } = useCurrentSubscriptionHelper();
    const { provider, activeCount } = useFirstSelectedProvider();

    const providerName = provider ? PackageProviderService.getDisplayName(provider) : undefined;
    const goToAddCarrier = () => navigate(SettingUrls.shippingProviders);

    return (
        <s-section padding="none">
            <div className="flex h-full flex-col px-4 py-3 max-md:gap-2 md:card-fix-height justify-between">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-heading-md font-semibold">{title}</div>
                    <div className="text-body-lg font-semibold">{planName}</div>
                </div>

                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-2xl font-bold">{usageText}</div>
                        {statusBadge && <s-badge {...statusBadge}>{statusBadge.label}</s-badge>}
                    </div>
                    {progress && <s-progress max={100} {...progress} />}
                </div>

                {description && <div className="text-body-sm text-text-subdued">{description}</div>}

                {provider ? (
                    <UsageProgressProviderRow
                        media={
                            <img
                                className="size-9 rounded-lg border border-border"
                                src={getPackageProviderIconUrl(provider)}
                                alt={providerName}
                            ></img>
                        }
                        name={providerName}
                        description={
                            carrierLimit === undefined
                                ? t('DASHBOARD.USAGE_PROGRESS_CARD.PROVIDER.CARRIERS_CONNECTED', {
                                      count: activeCount,
                                  })
                                : t('DASHBOARD.USAGE_PROGRESS_CARD.PROVIDER.CARRIERS_OF_LIMIT', {
                                      count: activeCount,
                                      limit: carrierLimit,
                                      plan: currentSubscription?.name,
                                  })
                        }
                        action={
                            <s-button onClick={() => navigate(SettingUrls.orders)}>
                                {t('DASHBOARD.USAGE_PROGRESS_CARD.PROVIDER.VIEW_ORDERS')}
                            </s-button>
                        }
                    />
                ) : (
                    <UsageProgressProviderRow
                        media={
                            <button
                                type="button"
                                className="flex size-9 cursor-pointer items-center justify-center rounded-lg border border-border bg-transparent hover:bg-bg-surface-hover"
                                aria-label={t('DASHBOARD.USAGE_PROGRESS_CARD.NO_PROVIDER.ADD_CARRIER')}
                                onClick={goToAddCarrier}
                            >
                                <s-icon type="plus"></s-icon>
                            </button>
                        }
                        name={t('DASHBOARD.USAGE_PROGRESS_CARD.NO_PROVIDER.TITLE')}
                        description={t('DASHBOARD.USAGE_PROGRESS_CARD.NO_PROVIDER.DESCRIPTION')}
                        action={
                            <s-link onClick={goToAddCarrier}>
                                {t('DASHBOARD.USAGE_PROGRESS_CARD.NO_PROVIDER.ADD_CARRIER')}
                            </s-link>
                        }
                    />
                )}

                {!!actions?.length && (
                    <div className="flex flex-wrap gap-3">
                        {actions.map((action) => (
                            <ActionButton key={action.label} action={action} />
                        ))}
                    </div>
                )}
            </div>
        </s-section>
    );
};
